<template>
  <div class="betslip-footer-wrapper">
    <div class="future-bets"
         :class="{disabled: bettingLocked}"
         v-if="config.futureBets">
      <div class="footer-dropdown"
           v-click-outside="closeDropdown"
           :class="{expanded: expanded}"
           @click="expandDropdown">
        <label>
          {{ 'general_future_bet' | translate }}
        </label>
        <div class="value">
          {{ futureBets > maxFutureBets ? 1 : futureBets }}
        </div>
        <i class="icon"
           :class="dropdownIcon">
        </i>
        <Scrollable>
          <div class="dropdown-list"
             v-if="expanded && !bettingLocked && !isMob">
             <div class="dropdown-item"
               @click="changeFutureBets(option)"
               v-for="(option, key) in maxFutureBets"
               :key="key">
               {{ option }}
              </div>
            </div>
        </Scrollable>
      </div>
    </div>
    <div class="total-stake"
         :class="{disabled: bettingLocked}"
         @click="setTotalPaymentFocus"
         v-if="config.totalStake">
      <div class="label">
        {{'general_total_stake' | translate}}
      </div>
      <input class="value no-spinner"
             ref="totalPayment"
             data-layout="numeric"
             inputmode="numeric"
             @keyup.enter="changeTotalStake"
             @blur="changeTotalStake"
             :value="totalPayment.toFixed(2)"
             type="number">
      <i class="icon icon-backspace-a" @click="clear()"></i>
    </div>
    <BetslipTaxInfo :taxDisabled="taxDisabled"></BetslipTaxInfo>
    <div class="payin-toolbar">
      <button class="button empty-betslip"
              :class="{disabled: !ticket.bets.length || bettingLocked}"
              :disabled="!ticket.bets.length || bettingLocked"
              @click="removeBets">
        <i class="icon icon-trash"></i>
      </button>
      <button class="button important login-button"
              v-if="!player.loggedIn"
              @click="login()">
        {{'general_sign_in' | translate}}
      </button>
      <button class="button important payin-button"
              v-if="player.loggedIn"
              :class="{disabled: isPayinDisabled}"
              :disabled="isPayinDisabled"
              @click="payinTicket()">
        {{'general_pay_in' | translate}}
      </button>
    </div>
    <stake-fade-transition>
      <StakeBox v-if="showStakeBox && !bettingLocked"
                @close="toggleStakeBox"
                :predefinedStakes="predefinedStakes">
      </StakeBox>
    </stake-fade-transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ClickOutside from 'vue-click-outside';
import eventBus from '@/utility/eventBus';
import BetslipTaxInfo from './BetslipTaxInfo';
import StakeBox from './StakeBox';
import StakeFadeTransition from './StakeFadeTrasition';
import Scrollable from '../Scrollable';

export default {
  name: 'BetslipFooter',
  directives: {
    ClickOutside,
  },
  components: {
    BetslipTaxInfo,
    StakeBox,
    StakeFadeTransition,
    Scrollable,
  },
  props: {
    payinDisabled: {
      type: Boolean,
      default: true,
    },
    bettingLocked: {
      type: Boolean,
      default: false,
    },
    predefinedStakes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      expanded: false,
      value: null,
      showStakeBox: false,
      totalStakeActive: false,
    };
  },
  computed: {
    ...mapGetters([
      'meta',
      'ticket',
      'showBetslip',
      'betslipConfig',
      'payinForbidden',
      'ticketUnpayable',
      'maxBetAmountErr',
      'rules',
      'futureBets',
      'player',
      'translations',
      'totalPayment',
      'maxFutureBets',
      'bettingDisabled',
      'minPayment',
    ]),
    isMob() {
      return window.innerWidth <= 660;
    },
    dropdownIcon() {
      return this.expanded ? 'icon-arrow-up-a' : 'icon-arrow-down-a';
    },
    taxDisabled() {
      return this.ticket.config.taxes.payin.hideTax;
    },
    totalStakeLabel() {
      return this.$options.filters.translate('general_total_stake');
    },
    config() {
      return this.betslipConfig;
    },
    isPayinDisabled() {
      return this.payinForbidden || this.ticketUnpayable
        || !this.ticket.bets.length || this.bettingLocked || this.bettingDisabled
    },
  },
  methods: {
    ...mapActions([
      'emptyBetslip',
      'setTotalPayment',
      'setFutureBets',
      'showHideBetslip',
      'payin',
      'updateNotifications',
    ]),
    toggleStakeBox() {
      if (!this.totalStakeActive) this.showStakeBox = false;
    },
    setTotalPaymentFocus() {
      if (this.bettingLocked) return;
      this.$refs.totalPayment.select();
      this.totalStakeActive = true;
      this.showStakeBox = true;
    },
    removeBets() {
      eventBus.$emit('clearBetslip');
      this.emptyBetslip();
    },
    updateTotalStake(value) {
      let stake = this.totalPayment;
      stake += value;
      this.setTotalPayment(stake);
    },
    changeTotalStake(e) {
      this.totalStakeActive = false;
      const stake = Number(e.target.value).toFixed(2);
      if (stake !== this.totalPayment) {
        this.setTotalPayment(stake);
      }

      if (this.payinForbidden) {
        const msg = this.translations.general_max_bet_amount_rule.supplant({
          value: this.rules.maxBetAmount.value,
        });
        this.updateNotifications({
          status: 'error',
          message: msg,
        });
      }
    },
    changeFutureBets(futureBets) {
      this.setFutureBets(futureBets);
    },
    login() {
      eventBus.$emit('loginRequired');
    },
    payinTicket() {
      eventBus.$emit('payin');
      this.payin(this.ticket);
    },
    expandDropdown() {
      if (this.bettingLocked) return;
      this.expanded = !this.expanded;
      if (this.isMob) {
        eventBus.$emit('openFutureBets', this.maxFutureBets);
      }
    },
    closeDropdown() {
      this.expanded = false;
    },
    setSelection(pick) {
      this.value = pick;
      this.closeDropdown();
      this.$emit('setSelectionOption', pick);
    },
    clear() {
      this.setTotalPayment(this.minPayment);
    },
  },
  created() {
    eventBus.$on('setFutureBets', (futureBets) => {
      this.changeFutureBets(futureBets);
    });
  },
};
</script>

<style lang="scss">
  .betslip-footer-wrapper {
    color: var(--text-primary-1);
    float: left;
    width: 100%;
    padding: 10px;
    background-color: var(--betslip-footer-bg, var(--bg-color-1));
    position: relative;

    .stake-box {
      height: 144px;
      bottom: 106px;
      left: 0;
    }

    .total-stake {
      width: 48%;
      height: 40px;
      float: right;
      line-height: 40px;
      position: relative;
      border: 1px solid #373737;
      border-radius: 2px;
      background-color: var(--input-bg);

      &.disabled {
        opacity: .4;
      }

      .label {
        left: 14px;
        position: absolute;
        z-index: 1;
        top: -10px;
        font-size: 12px;
        color: var(--text-primary-2);
      }

      input {
        width: 100%;
        border-radius: 0 2px 2px 0;
        outline: none;
        border: 0;
        color: var(--text-primary-1);
        background-color: var(--input-bg);
        font-size: 14px;
        text-align: left;
        padding: 0 14px;
        font-family: 'Roboto', sans-serif;
        top: 19px;
        position: absolute;
      }

      input[type=number]::-webkit-inner-spin-button,
      input[type=number]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      input[type=number] {
        -moz-appearance: textfield;
      }

      .icon {
        position: absolute;
        right: 14px;
        top: 11px;
        cursor: pointer;
      }
    }

    .future-bets {
      float: left;
      width: 48%;
      height: 40px;

      &.disabled {
        opacity: .4;
      }

      .footer-dropdown {
        float: left;
        width: 100%;
        height: 100%;
        border-radius: 2px;
        background-color: var(--dropdown-bg, var(--button-bg));
        color: var(--text-primary-2);
        font-size: 14px;
        padding: 0 14px;
        position: relative;
        margin: 0;
        transition: all .5s ease;

        &:hover {
          background-color: var(--dropdown-hover-bg, var(--button-hover-bg));
        }

        &:active {
          background-color: var(--dropdown-pressed-bg, var(--button-pressed-bg));
        }

        &.disabled {
          &.disabled {
            background-color: var(--dropdown-pressed-bg, var(--button-pressed-bg));
            color: var(--text-primary-3);
          }
        }

        &.expanded {
          &:hover {
            background-color: var(--dropdown-bg, var(--button-bg));
          }
        }

        label {
          left: 14px;
          top: 4px;
          font-size: 12px;
          position: absolute;
          cursor: pointer;
        }

        .value {
          position: absolute;
          top: 20px;
          font-size: 14px;
          color: var(--text-primary-1);
        }

        .icon {
          position: absolute;
          right: 0;
          line-height: 40px;
          margin-right: 8px;
          text-align: center;
          cursor: pointer;
          border: none;
          font-size: 18px;
        }

        .dropdown-list {
          position: absolute;
          top: 42px;
          width: 100%;
          background-color: var(--button-bg);
          left: 0;
          z-index: 1;
          transition: all .5s ease;
          max-height: 250px;

          .dropdown-item {
            float: left;
            width: 100%;
            height: 40px;
            cursor: pointer;
            line-height: 40px;
            padding: 0 16px;
            transition: all .5s ease;
            text-align: left;

            &:hover {
              background-color: var(--button-hover-bg);
            }
          }
        }

        .future-bets-modal {
          .title {
            font-size: 18px;
            font-weight: 500;
            color: var(--text-primary-1, #efefef);
          }
        }
      }
    }

    .payin-toolbar {
      float: left;
      width: 100%;

      .empty-betslip {
        float: left;
        width: 40px;
        height: 40px;
        font-size: 18px;
        transition: all .5s ease;

        &.disabled {
          opacity: .4;
        }
      }

      .important {
        width: 236px;
        height: 40px;
        float: right;
        font-size: 14px;
      }

      .payin-button,
      .login-button {
        background-color: var(--payin-button-bg, var(--brand-bg));
        color: var(--payin-button-text-color, var(--brand-text-color));
        transition: all .5s ease;

        &:hover {
          background-color: var(--payin-button-hover-bg);
        }

        &:active {
          background-color: var(--payin-button-pressed-bg);
        }

        &.disabled {
          opacity: .7;
        }
      }
    }

    .message {
      color: var(--text-primary-1);
      font-size: 0.875em;
      padding: 10px 0;
      float: left;
      width: 100%;
    }
  }

  @media (min-width: 320px) and (max-width: 960px) {
    .betslip-footer-wrapper {
      display: flex;
      flex-wrap: wrap;
      .total-stake, .important {
        display: none;
      }
      .future-bets {
        flex-basis: 80%;
        order: 1;
        .footer-dropdown {
          .dropdown-list {
            top: -201px;
            height: 200px;
            overflow: auto;
          }
        }
      }
      .betslip-tax-info-wrapper {
        flex-basis: 100%;
        order: 3;
      }
      .payin-toolbar {
        flex-basis: 20%;
        order: 2;
        float: right;
        .empty-betslip {
          float: right;
          width: 90%;
        }
      }
    }
  }
</style>
